import { Atendimento, AttendanceData } from "../interfaces/AttendanceData";
import attendanceApi from "../services/api/attendance";
import React, { createContext, useEffect, useState } from "react";
import UserService from "@/services/UserService";
import { UserRole } from "@/enums/UserRole";
import { safeJsonParse } from "@/utils/safeJsonParse";

export type dadosPessoais = {
  nome: string;
  telefone: string;
  email?: string;
  origem: string;
  cpf?: string;
  cnpj?: string;
  dataNascimento?: string;
};

export type dadosPessoaJuridica = {
  inscricaoEstadual?: string;
  inscricaoEstadualIsento?: boolean;
  dataFundacao?: string;
};

export type endereco = {
  id?: number;
  entregaId?: number;
  cep?: string;
  rua?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
};

export type enderecoEntrega = {
  id?: number;
  cep?: string;
  rua?: string;
  numero?: string;
  complemento?: string;
  bairro?: string;
  cidade?: string;
  estado?: string;
};

export type client = {
  dadosPessoais: dadosPessoais;
  dadosPessoaJuridica?: dadosPessoaJuridica;
  endereco?: endereco;
  enderecoEntrega?: enderecoEntrega;
  createdAt?: string;
  atendimentos?: [];
  id?: number;
  atendimentoAtualId?: any;
  briefingId?: number;
  inscricaoEstadualIsento?: boolean;
  chatLink?: string | null;
};

export interface attendanceDataRequestDTO {
  id: any;
  estado: string;
  atendimento_id: string;
  cliente_id: number;
  briefing_id: number;
  criadoEm: string;
  Cliente: clientRequestDTO;
}

export interface clientRequestDTO {
  id: number;
  nome: string;
  telefone: string;
  email?: string;
  origem: string;
  cpf?: string;
  cnpj?: string;
  dataNascimento?: string;
  inscricaoEstadual?: string;
  isentoInscricaoEstadual?: boolean;
  enderecoId?: number;
  enderecoEntregaId?: number;
  criadoEm?: string;
  arquivado?: boolean;
  userId?: string | number;
  endereco?: {
    id: number;
    cep?: string;
    rua?: string;
    numero?: string;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
  };
  enderecoEntrega?: {
    id: number;
    cep?: string;
    rua?: string;
    numero?: string;
    complemento?: string;
    bairro?: string;
    cidade?: string;
    estado?: string;
  };
}

interface ClientContextType {
  client: client;
  setClient: (client: client) => void;
  resetClient: () => void;
  clientes: AttendanceData[];
  setClientes: (clientes: AttendanceData[]) => void;
  atendimentos: Atendimento[];
  setAtendimentos: (atendimentos: Atendimento[]) => void;
  reloadClientes: (filtro?: string, campoFiltro?: string, arquivamento?: boolean) => void;
  formatAddress: (endereco?: any, enderecoEntrega?: any) => string | undefined;
}

interface clientContextProps {
  children: React.ReactNode;
}

const ClientContext = createContext<ClientContextType | undefined>(undefined);

export const ClientContextProvider = ({ children }: clientContextProps) => {
  const defaultClient: client = {
    dadosPessoais: {
      nome: "",
      telefone: "+55",
      origem: "",
    },
    endereco: {
      id: undefined,
      cep: "",
      rua: "",
      numero: "",
      complemento: "",
      bairro: "",
      cidade: "",
      estado: "",
    },
    enderecoEntrega: undefined,
  };

  const [client, setClient] = useState<client>(defaultClient);
  const [clientes, setClientes] = useState<AttendanceData[]>([]);
  const [atendimentos, setAtendimentos] = useState<Atendimento[]>([]);

  useEffect(() => { }, [client]);

  const resetClient = () => {
    setClient(defaultClient);
  };

  const processarClientes = (attendances: attendanceDataRequestDTO[]) => {
    return attendances.map((attendance: attendanceDataRequestDTO) => {
      return {
        id: attendance.id,
        Client: {
          id: attendance.Cliente.id,
          atendimentoAtualId: attendance.id,
          dadosPessoais: {
            nome: attendance.Cliente.nome,
            email: attendance.Cliente.email,
            origem: attendance.Cliente.origem,
            telefone: attendance.Cliente.telefone,
            cpf: attendance.Cliente.cpf,
            dataNascimento: attendance.Cliente.dataNascimento,
            cnpj: attendance.Cliente.cnpj,
          },
          dadosPessoaJuridica: {
            inscricaoEstadual: attendance.Cliente.inscricaoEstadual,
            inscricaoEstadualIsento: attendance.Cliente.isentoInscricaoEstadual,
            dataFundacao: attendance.Cliente.dataNascimento,
          },
          endereco: {
            id: attendance.Cliente.endereco?.id ?? undefined,
            cep: attendance.Cliente.endereco?.cep ?? "",
            rua: attendance.Cliente.endereco?.rua ?? "",
            numero: attendance.Cliente.endereco?.numero ?? "",
            complemento: attendance.Cliente.endereco?.complemento ?? "",
            bairro: attendance.Cliente.endereco?.bairro ?? "",
            cidade: attendance.Cliente.endereco?.cidade ?? "",
            estado: attendance.Cliente.endereco?.estado ?? "",
          },
          enderecoEntrega: attendance.Cliente.enderecoEntrega && {
            id: attendance.Cliente.enderecoEntrega?.id ?? undefined,
            cep: attendance.Cliente.enderecoEntrega?.cep ?? "",
            rua: attendance.Cliente.enderecoEntrega?.rua ?? "",
            numero: attendance.Cliente.enderecoEntrega?.numero ?? "",
            complemento: attendance.Cliente.enderecoEntrega?.complemento ?? "",
            bairro: attendance.Cliente.enderecoEntrega?.bairro ?? "",
            cidade: attendance.Cliente.enderecoEntrega?.cidade ?? "",
            estado: attendance.Cliente.enderecoEntrega?.estado ?? "",
          },
          createdAt: attendance.Cliente.criadoEm,
        },
        attendant_id: attendance.atendimento_id,
        client_id: attendance.cliente_id,
        state: attendance.estado,
        atendimentoCriadoEm: attendance.criadoEm,
      };
    })
  }

  const reloadClientes = async (
    filtro?: string,
    campoFiltro?: string,
    arquivamento?: boolean
  ) => {
    try {
      const user = UserService.getUser();
      const attendantRaw = localStorage.getItem("attendant");
      const attendant = attendantRaw ? safeJsonParse(attendantRaw) : null;

      if (filtro && campoFiltro) {
        await filtrarClientes(filtro, campoFiltro, arquivamento);
        return;
      }

      const isProjetista = user?.role?.includes(UserRole.PROJETISTA);
      const atendenteId = isProjetista ? attendant?.id : undefined;

      const { data } = await attendanceApi.listAttendances(atendenteId, arquivamento);
      const { attendances = [] } = data || {};

      setClientes(processarClientes(attendances));
      setAtendimentos(attendances);
    } catch (err) {
      console.error("Erro ao obter os atendimentos:", err);
      throw err;
    }
  };

  async function filtrarClientes(filtro?: string, campoFiltro?: string, arquivamento?: boolean) {
    try {
      const params = filtro === "Nome"
        ? { nome: campoFiltro }
        : { telefone: campoFiltro };

      const resposta = await attendanceApi.buscarClientePorNomeOuTelefone(
        params.nome,
        params.telefone,
        arquivamento
      )
      setClientes(processarClientes(resposta.data))
    } catch (err) {
      console.log(err);
    }
  }

  const formatAddress = (
    endereco?: endereco,
    enderecoEntrega?: enderecoEntrega
  ) => {
    // Determina qual endereço será formatado
    const addressToFormat = enderecoEntrega || endereco;

    // Se nenhum endereço for passado, retorna uma mensagem padrão
    if (!addressToFormat) {
      return "Endereço não informado";
    }

    const { rua, numero, complemento, bairro, cidade, estado, cep } = addressToFormat;

    // Monta o endereço formatado ignorando campos vazios
    const formattedAddress = [
      rua && `${rua}`,
      numero && `${numero}`,
      complemento && `${complemento}`,
      bairro && `${bairro}`,
      cidade && `${cidade}`,
      estado && `${estado}`,
      cep && `CEP: ${cep}`,
    ]
      .filter(Boolean) // Remove valores que são null, undefined ou strings vazias
      .join(", "); // Junta os valores com ", "

    return formattedAddress || "Endereço não informado";
  };

  return (
    <ClientContext.Provider
      value={{
        client,
        setClient,
        resetClient,
        clientes,
        setClientes,
        atendimentos,
        setAtendimentos,
        reloadClientes,
        formatAddress,
      }}
    >
      {children}
    </ClientContext.Provider>
  );
};

export const useClient = () => {
  const context = React.useContext(ClientContext);
  if (context === undefined) {
    throw new Error(
      "useClient precisa ser usado dentro de um ClientContextProvider"
    );
  }
  return context;
};
